import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Drawer, MenuItem, TablePagination, TextField, Typography } from '@mui/material';
import { Title } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { isAdmin } from '../../core/roles.ts';
import { money } from '../../utils/money.ts';
import { errorText } from '../../services/shabbat/shabbatService.ts';
import CreditReportService, { type Restaurant } from '../../services/credit/creditReportService.ts';
import OrderHistoryService, {
    type HistoryOrder, type OrderHistory, type OrderHistoryOutcome, type OrderStatus,
} from '../../services/order/orderHistoryService.ts';
import { CustomerChips } from '../../components/customer/CustomerChips.tsx';
import { WhatsAppButton } from '../../components/customer/WhatsAppButton.tsx';
import { isoDate } from '../credit/dateRanges.ts';
import { PastOrderPanel } from './OrderDetailsPanel.tsx';
import { isOnSite, onSiteColours, onSiteDoneLabel, onSiteWhen, orderTypeName } from '../../components/order/DineIn.tsx';

const service = new OrderHistoryService();
const restaurantsService = new CreditReportService();
const REFRESH_MS = 60_000;

type RangeKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';
const RANGES: [RangeKey, string][] = [
    ['today', 'Today'], ['yesterday', 'Yesterday'], ['last7', 'Last 7 days'], ['last30', 'Last 30 days'], ['custom', 'Custom'],
];

const daysAgo = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return isoDate(d);
};

const rangeOf = (key: RangeKey): { from: string; to: string } | null => {
    switch (key) {
        case 'today': return { from: daysAgo(0), to: daysAgo(0) };
        case 'yesterday': return { from: daysAgo(1), to: daysAgo(1) };
        case 'last7': return { from: daysAgo(6), to: daysAgo(0) };
        case 'last30': return { from: daysAgo(29), to: daysAgo(0) };
        default: return null;
    }
};

type Chip = 'all' | 'new' | 'returning' | 'app' | 'web';
type TypeFilter = 'all' | 'delivery' | 'dine_in' | 'pickup';
const TYPE_FILTERS: [TypeFilter, string][] = [['all', 'All types'], ['delivery', 'Delivery'], ['dine_in', 'Dine in'], ['pickup', 'Pickup']];

/** Neutral words only: a restaurant learns a courier is coming, never who or which service. */
const STATUS: Record<OrderStatus, [string, string, string]> = {
    PAID: ['New', '#FEF3C7', '#92400E'],
    ACCEPTED: ['Preparing', '#DBEAFE', '#1E40AF'],
    IN_PREPARATION: ['Preparing', '#DBEAFE', '#1E40AF'],
    ACCEPTED_BY_COURIER: ['Preparing', '#DBEAFE', '#1E40AF'],
    READY_FOR_PICKUP: ['Ready', '#DDF4F1', '#03676A'],
    ON_THE_WAY: ['On the way', '#DDF4F1', '#03676A'],
    DELIVERED: ['Delivered', '#F3F4F6', '#6B7280'],
    CANCELLED: ['Cancelled', '#FEE2E2', '#B91C1C'],
};

const OUTCOME: Record<Exclude<OrderHistoryOutcome, 'OPEN'>, [string, string, string]> = {
    DELIVERED: ['Delivered', '#F3F4F6', '#374151'],
    REJECTED: ['Rejected', '#FEF3C7', '#92400E'],
    CANCELLED: ['Cancelled', '#FEE2E2', '#B91C1C'],
};

const OUTCOME_FILTERS: [OrderHistoryOutcome | null, string, keyof OrderHistory['outcomes']][] = [
    [null, 'All', 'all'], ['OPEN', 'In progress', 'open'], ['DELIVERED', 'Delivered', 'delivered'],
    ['REJECTED', 'Rejected', 'rejected'], ['CANCELLED', 'Cancelled', 'cancelled'],
];

/** "14:05", or "Thu 23 Oct · 14:05" when the range covers more than one day; in the restaurant's own clock. */
const placed = (iso: string | null, timeZone: string, withDay: boolean) => {
    if (!iso) return '—';
    const opts: Intl.DateTimeFormatOptions = withDay
        ? { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
        : { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
    try {
        return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone }).format(new Date(iso)).replace(',', ' ·');
    } catch {
        return new Intl.DateTimeFormat('en-GB', opts).format(new Date(iso)).replace(',', ' ·');
    }
};

const itemsText = (o: HistoryOrder) => o.items
    .map(it => `${it.quantity}× ${it.name ?? ''}${it.extras && it.extras.length ? ` (${it.extras.join(', ')})` : ''}`)
    .join(', ');

const Pill: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
    <Box component="button" type="button" onClick={onClick} sx={{
        height: 34, borderRadius: '17px', px: 1.9, fontSize: 13.5, cursor: 'pointer', font: 'inherit',
        display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
        bgcolor: on ? '#24BBAA' : '#fff', color: on ? '#fff' : '#374151', fontWeight: on ? 700 : 400,
        border: on ? '1px solid #24BBAA' : '1px solid #E5E7EB', '&:hover': { borderColor: '#24BBAA' },
    }}>
        {children}
    </Box>
);

const Badge: React.FC<{ label: string; bg: string; color: string }> = ({ label, bg, color }) => (
    <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color, bgcolor: bg, borderRadius: '10px', px: 1.25, py: 0.4, whiteSpace: 'nowrap' }}>
        {label}
    </Box>
);

/**
 * Order history (Ariel, 23 Oct 2026): Today's orders and Past orders in one
 * page. Default range is today. Admins see every restaurant together with a
 * Venue column, or pick one; a restaurant sees its own orders and the FOOD
 * total only - the server sends nothing else.
 */
const OrderHistoryPage = () => {
    const admin = isAdmin();
    const [params, setParams] = useSearchParams();
    const venue = admin ? (Number(params.get('venue')) || null) : null;

    const [restaurants, setRestaurants] = useState<Restaurant[] | null>(admin ? null : []);
    const [range, setRange] = useState<RangeKey>('today');
    const [from, setFrom] = useState(daysAgo(0));
    const [to, setTo] = useState(daysAgo(0));
    const [outcome, setOutcome] = useState<OrderHistoryOutcome | null>(null);
    const [chip, setChip] = useState<Chip>('all');
    const [orderType, setOrderType] = useState<TypeFilter>('all');
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [data, setData] = useState<OrderHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState<string | null>(null);
    const [openId, setOpenId] = useState<number | null>(null);

    useEffect(() => {
        if (!admin) return;
        restaurantsService.restaurants().then(setRestaurants).catch(() => setRestaurants([]));
    }, [admin]);

    const query = useMemo(() => ({
        locationId: venue,
        from, to, outcome,
        customer: chip === 'new' || chip === 'returning' ? chip : null,
        src: chip === 'app' || chip === 'web' ? chip : null,
        type: orderType === 'all' ? null : orderType,
    }), [venue, from, to, outcome, chip, orderType]);

    const load = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        try {
            setData(await service.list(query, page * perPage, page * perPage + perPage));
            setFailed(null);
        } catch (e) {
            setFailed(errorText(e, 'Could not load the orders.'));
        } finally {
            setLoading(false);
        }
    }, [query, page, perPage]);

    useEffect(() => {
        load();
        // Live while the range reaches today: new orders keep arriving.
        if (to < daysAgo(0)) return;
        const timer = window.setInterval(() => load(true), REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [load, to]);

    const pickRange = (key: RangeKey) => {
        setRange(key);
        const r = rangeOf(key);
        if (r) { setFrom(r.from); setTo(r.to); }
        setPage(0);
    };

    const chooseVenue = (id: number | null) => {
        const next = new URLSearchParams(params);
        if (id) next.set('venue', String(id)); else next.delete('venue');
        setParams(next, { replace: true });
        setPage(0);
    };

    const all = !!data?.allVenues;
    const multiDay = from !== to;
    const rows = data?.orders ?? [];
    const s = data?.summary;
    const food = s && s.food.length ? s.food.map(f => money(f.amount, f.currency)).join(' + ') : money(0, data?.currency ?? 'EUR');
    // NC/RC and app/website are admin only (Ariel, 25 Oct 2026): the server leaves them out for a restaurant.
    const customerSplit = admin && s?.newCustomers != null;
    const tiles = s ? [
        { label: range === 'today' ? 'Orders today' : 'Orders', value: String(s.orders), sub: `${food} food` },
        ...(customerSplit ? [
            { label: 'New customers (NC)', value: String(s.newCustomers), sub: 'first order on Vello' },
            { label: 'Returning (RC)', value: String(s.returningCustomers), sub: s.orders ? `${Math.round((s.returningCustomers ?? 0) / s.orders * 100)}% of orders` : '' },
            { label: 'App / Website', value: `${(s.ios ?? 0) + (s.android ?? 0)} / ${s.web ?? 0}`, sub: `${s.ios ?? 0} iOS · ${s.android ?? 0} Android` },
        ] : []),
    ] : [];
    const chips: [Chip, string, number | undefined][] = customerSplit ? [
        ['all', 'All', s?.orders], ['new', 'New (NC)', s?.newCustomers], ['returning', 'Returning (RC)', s?.returningCustomers],
        ['app', 'App', s ? (s.ios ?? 0) + (s.android ?? 0) : undefined], ['web', 'Website', s?.web],
    ] : [];
    const venueName = all ? 'All venues' : (data?.restaurantName ?? restaurants?.find(r => r.id === venue)?.name ?? '');

    // Delivery and service fees live in the order's drawer (admins); the table keeps what fits at 1440 px.
    // [template, its least width]: the card scrolls sideways only below their sum (a phone).
    const cols: [string, number][] = [
        ['105px', 105],
        ...(all ? [['minmax(105px, 1fr)', 105] as [string, number]] : []),
        ['minmax(165px, 1.4fr)', 165], ['95px', 95], ['minmax(100px, 1.6fr)', 100], ['85px', 85],
        ...(admin ? [['90px', 90], ['90px', 90]] as [string, number][] : []),
        [multiDay ? '120px' : '64px', multiDay ? 120 : 64], ['110px', 110],
    ];
    const columns = cols.map(c => c[0]).join(' ');
    const minWidth = cols.reduce((a, c) => a + c[1], 0) + (cols.length - 1) * 10 + 36;

    return (
        // contain: the wide table scrolls inside its card instead of widening the page past the sidebar.
        <Box sx={{ pb: 6, bgcolor: '#F3F5F4', minHeight: 'calc(100vh - 72px)', width: '100%', minWidth: 0, maxWidth: '100vw', overflowX: 'hidden', contain: 'inline-size' }}>
            <Title title="Order history" />
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                px: { xs: 2, md: 3.5 }, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #E5E7EB',
            }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>Order history</Typography>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                        {venueName ? `${venueName} · ` : ''}{admin ? 'Orders with who’s new, where they ordered from and how they ended' : 'Your orders and how they ended'}
                    </Typography>
                </Box>
                {admin && restaurants && (
                    <TextField select size="small" label="Venue" value={venue ?? 0} onChange={e => chooseVenue(Number(e.target.value) || null)}
                        sx={{ minWidth: 240, bgcolor: '#fff' }}>
                        <MenuItem value={0}>All venues</MenuItem>
                        {restaurants.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                    </TextField>
                )}
            </Box>

            <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {RANGES.map(([key, label]) => (
                        <Pill key={key} on={range === key} onClick={() => pickRange(key)}>{label}</Pill>
                    ))}
                    {range === 'custom' && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <TextField size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
                                onChange={e => { if (e.target.value) { setFrom(e.target.value); setPage(0); } }} sx={{ bgcolor: '#fff', width: 160 }} />
                            <TextField size="small" type="date" label="To" value={to} InputLabelProps={{ shrink: true }}
                                onChange={e => { if (e.target.value) { setTo(e.target.value); setPage(0); } }} sx={{ bgcolor: '#fff', width: 160 }} />
                        </Box>
                    )}
                </Box>

                {failed && <Alert severity="error" sx={{ maxWidth: 760, mt: 2 }}
                    action={<Button color="inherit" size="small" onClick={() => load()}>Retry</Button>}>{failed}</Alert>}
                {!data && !failed && <Box sx={{ mt: 3 }}><CircularProgress size={24} /></Box>}
                {data && (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.75, mt: 2.5 }}>
                            {tiles.map(k => (
                                <Box key={k.label} sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', px: 2.25, py: 2 }}>
                                    <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>{k.label}</Typography>
                                    <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#111827', mt: 0.5, lineHeight: 1.2 }}>{k.value}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: '#03676A', mt: 0.25, minHeight: 18 }}>{k.sub}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3 }}>
                            {OUTCOME_FILTERS.map(([key, label, count]) => (
                                <Pill key={label} on={outcome === key} onClick={() => { setOutcome(key); setPage(0); }}>
                                    {label} · {data.outcomes[count]}
                                </Pill>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                            {TYPE_FILTERS.map(([key, label]) => (
                                <Pill key={key} on={orderType === key} onClick={() => { setOrderType(key); setPage(0); }}>
                                    {label}
                                    {key === 'dine_in' && s?.dineIn != null ? ` · ${s.dineIn}` : ''}
                                    {key === 'pickup' && s?.pickup != null ? ` · ${s.pickup}` : ''}
                                </Pill>
                            ))}
                        </Box>
                        {chips.length > 0 && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                            {chips.map(([key, label, count]) => (
                                <Pill key={key} on={chip === key} onClick={() => { setChip(key); setPage(0); }}>
                                    {label}{count !== undefined ? ` · ${count}` : ''}
                                </Pill>
                            ))}
                        </Box>}

                        <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', mt: 1.75, overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
                            <Box sx={{ minWidth }}>
                                <Box sx={{
                                    display: 'grid', gridTemplateColumns: columns, gap: 1.25, px: 2.25, py: 1.5, fontSize: 12, fontWeight: 700,
                                    color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFBFB',
                                }}>
                                    <span>Order</span>{all && <span>Venue</span>}<span>Customer</span><span>Type</span><span>Items</span><span>Food total</span>
                                    {admin && <><span>Order total</span><span>Delivered by</span></>}
                                    <span>Placed</span><span>Status</span>
                                </Box>
                                {rows.length === 0 && (
                                    <Typography sx={{ p: 5, textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
                                        {data.outcomes.all === 0 ? (range === 'today' ? 'No orders yet today.' : 'No orders in these days.') : 'No orders match.'}
                                    </Typography>
                                )}
                                {rows.map(o => {
                                    const finished = o.outcome !== 'OPEN' ? OUTCOME[o.outcome] : null;
                                    const onSite = isOnSite(o.orderType);
                                    let [label, bg, color] = finished ?? STATUS[o.status] ?? [o.status, '#F3F4F6', '#6B7280'];
                                    // An on-site order "delivered" is one the restaurant served (dine in) or handed over at the counter (pickup).
                                    if (onSite && o.outcome === 'DELIVERED') label = onSiteDoneLabel(o.orderType);
                                    const onSiteTime = onSite ? onSiteWhen(o.orderType, o.dineIn) : null;
                                    const typeColours = onSiteColours(o.orderType);
                                    return (
                                        <Box key={o.id} role="button" tabIndex={0} onClick={() => setOpenId(o.id)}
                                            onKeyDown={e => { if (e.key === 'Enter') setOpenId(o.id); }}
                                            sx={{
                                                display: 'grid', gridTemplateColumns: columns, gap: 1.25, px: 2.25, py: 1.6, alignItems: 'center', cursor: 'pointer',
                                                borderBottom: '1px solid #F0F2F1', fontSize: 14, color: '#111827', '&:hover': { bgcolor: '#FAFBFB' },
                                            }}>
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{o.dailyNumber ?? o.orderNumber}</Typography>
                                                {o.dailyNumber && <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>{o.orderNumber}</Typography>}
                                            </Box>
                                            {all && <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#374151', minWidth: 0 }} noWrap title={o.restaurantName ?? ''}>{o.restaurantName ?? '—'}</Typography>}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography noWrap sx={{ fontWeight: 700, fontSize: 14 }}>{o.customerName ?? '—'}</Typography>
                                                <CustomerChips prev={o.prev} src={o.src} unit="order" />
                                                {o.customerPhone && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                                                        <Typography sx={{ fontSize: 12, color: '#9CA3AF', direction: 'ltr', textAlign: 'start' }}>{o.customerPhone}</Typography>
                                                        {admin && <WhatsAppButton phone={o.customerPhone} name={o.customerName} size={22} />}
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box>
                                                {onSite ? (
                                                    <>
                                                        <Badge label={orderTypeName(o.orderType)} bg={typeColours.light} color={typeColours.solid} />
                                                        {(onSiteTime || o.dineIn?.partySize) && (
                                                            <Typography sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.5 }}>
                                                                {[onSiteTime, o.dineIn?.partySize ? `party of ${o.dineIn.partySize}` : null].filter(Boolean).join(' · ')}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Typography sx={{ fontSize: 13, color: '#4B5563' }}>Delivery</Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{
                                                fontSize: 13, color: '#4B5563', lineHeight: 1.35, overflow: 'hidden',
                                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                            }}>{itemsText(o)}</Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{money(o.foodTotal, o.currency)}</Typography>
                                            {admin && <>
                                                <Typography sx={{ fontSize: 14, color: '#111827' }}>{money(o.orderTotal, o.currency)}</Typography>
                                                <Typography sx={{ fontSize: 13, color: '#4B5563' }} noWrap>
                                                    {onSite ? orderTypeName(o.orderType) : o.deliveredBy === 'WOLT' ? 'Wolt' : o.deliveredBy === 'VELLO_COURIER' ? (o.courierName ?? 'Own courier') : '—'}
                                                </Typography>
                                            </>}
                                            <Typography sx={{ fontSize: 13, color: '#4B5563' }}>{placed(o.placedAt, o.timeZone, multiDay)}</Typography>
                                            <Box>
                                                <Badge label={label} bg={bg} color={color} />
                                                {(o.outcome === 'REJECTED' || o.outcome === 'CANCELLED') && o.cancelReason && (
                                                    <Typography sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.5 }}>{o.cancelReason}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        <TablePagination
                            component="div"
                            count={data.total}
                            page={page}
                            rowsPerPage={perPage}
                            rowsPerPageOptions={[25, 50, 100]}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        />
                        <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.5 }}>
                            {customerSplit && 'NC = new customer, RC n = returning with n earlier orders on Vello (any venue). '}
                            Food total is what the customer paid for the food. Times are the venue’s own.
                            {to >= daysAgo(0) ? ' Updates every minute.' : ''} Click an order for its items and timeline.
                        </Typography>
                    </>
                )}
            </Box>

            <Drawer anchor="right" open={openId !== null} onClose={() => setOpenId(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
                {openId !== null && <PastOrderPanel orderId={openId} admin={admin} onClose={() => setOpenId(null)} />}
            </Drawer>
        </Box>
    );
};

export default OrderHistoryPage;
